/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import SeoHeader from '../components/shared/SeoHeader';
import SeoFooter from '../components/shared/SeoFooter';
import InstallCounter from '../components/InstallCounter';
import { marketingApi } from '../services/socialApi';
import GrowthFunnel from '../components/Download/GrowthFunnel';
import { getReferralCode } from '../hooks/useReferral';
import { NUNBA_DOWNLOAD_URL, NUNBA_RELEASE_URL, DOWNLOAD_PATHS } from '../config/downloads';
import PageMeta from '../components/shared/PageMeta';

// Download links route through our own /go/<asset>, which records the click
// (channel, campaign, recipient, bot flag) and then 302s to the real file, so
// the download is unchanged but attributable. Asset names come straight from
// the release, so `latest` keeps resolving as new builds land. See
// src/config/downloads.js for the canonical URLs.

// The universal installer: one Go binary per OS, double-clickable with NO
// Python and no setup. It detects the machine, then installs Nunba, installs
// HART OS, or writes HART OS to a USB and verifies it (the flashing the old
// hart_usb_flasher.py did, now built in). Built on every push and published on
// the HARTOS latest release. Verified live: exe, macos and linux all return
// 200. The raw hart_usb_flasher.py stays in the developer list.
const HARTOS_INSTALLERS = {
  windows: { file: 'hevolve-install.exe',   label: 'Installer for Windows' },
  mac:     { file: 'hevolve-install-macos', label: 'Installer for macOS' },
  linux:   { file: 'hevolve-install-linux', label: 'Installer for Linux' },
};
const HARTOS_FLASHER = 'hart_usb_flasher.py';
const hartos = (file) => `/go/hartos/${file}`;

// The container path. Image + default port come straight from the installer
// (deploy/installer/main.go: ghcr.io/hertz-ai/hartos:latest, port 6777).
const DOCKER_CMD = 'docker run -d -p 6777:6777 ghcr.io/hertz-ai/hartos:latest';

// Per-OS primary app download for the hero button. Every href is an asset
// verified to return 200, so none 404. Windows ships a native installer today;
// macOS and Linux use the double-click universal installer (it installs Nunba
// for you) because native .dmg / .deb / AppImage are not published yet. Windows
// is the prerendered default so the first paint matches the captured HTML; a
// non-Windows visitor gets their line swapped in AFTER mount by the effect,
// never during render, which keeps this prerendered page free of hydration
// mismatch.
const PRIMARY_BY_OS = {
  windows: { label: 'Download for Windows', href: DOWNLOAD_PATHS.win },
  mac:     { label: 'Download for macOS', href: DOWNLOAD_PATHS.mac },
  linux:   { label: 'Download for Linux (.deb)', href: DOWNLOAD_PATHS.deb },
  android: { label: 'Get Nunba on Google Play', href: DOWNLOAD_PATHS.android },
  // iOS gets the desktop installer, not the Play Store.
  //
  // This line used to read 'Get Nunba on Google Play' pointing at the Android
  // listing, so an iPhone visitor was offered a store their device cannot open.
  // There is no iOS build to link instead -- the matrix below says so honestly
  // with href: null -- and the truthful next-best action for someone on an
  // iPhone is the desktop app, which is what Nunba actually is.
  ios:     { label: 'Download for macOS', href: DOWNLOAD_PATHS.mac },
};

// The device matrix. Rendered in full and statically (all tiles always shown),
// so nobody lands on a page that only offers an OS they are not on, and there
// is no hydration risk. Each tile states the real, current state:
//   stable    Windows native installer, verified live
//   installer macOS / Linux via the double-click universal installer (native
//             builds are on the way: the Nunba mac/linux CI legs are failing)
//   play      Android and WearOS on Google Play
//   nightly   iOS, early access, no public link yet so it is not a live link
const DEVICES = [
  // "signed" was claimed on both of these and is NOT true (2026-09-22). The CI
  // signing step runs under continue-on-error and the repo has no Azure signing
  // secrets, so `Verify the installer is actually signed` takes its no-secrets
  // branch and passes with a WARNING: the measured verdict on the current green
  // build is `signature : NotSigned`, signer empty. Telling someone a binary is
  // signed when it is not is the one claim on this page a visitor cannot check
  // before they run it, so say what actually happens instead: Windows
  // SmartScreen will warn. Restore "signed" only when that step reports Valid.
  { key: 'windows', device: 'desktop', name: 'Windows', kind: 'Desktop · native installer', tone: 'stable',    chip: 'Stable',        fmt: 'Nunba_Setup.exe · ~435 MB · SmartScreen will warn',           href: DOWNLOAD_PATHS.win,       cta: 'Download' },
  { key: 'mac',     device: 'desktop', name: 'macOS',   kind: 'Desktop · native installer', tone: 'stable', chip: 'Stable', fmt: 'Nunba_Setup.dmg · unsigned, Gatekeeper will warn',           href: DOWNLOAD_PATHS.mac, cta: 'Download' },
  { key: 'linux',   device: 'desktop', name: 'Linux',   kind: 'Desktop · .deb / AppImage', tone: 'stable', chip: 'Stable', fmt: 'nunba_1.0.0_amd64.deb · AppImage', href: DOWNLOAD_PATHS.deb, cta: 'Download' },
  { key: 'android', device: 'phone',   name: 'Android', kind: 'Phone · Play Store', tone: 'play',    chip: 'On Google Play', fmt: 'com.hertzai.hevolve · auto-updates',            href: DOWNLOAD_PATHS.android,   cta: 'Open in Play' },
  { key: 'ios',     device: 'phone',   name: 'iOS',     kind: 'Phone · nightly build', tone: 'nightly', chip: 'Nightly',      fmt: 'TestFlight nightly · early access',             href: null,                     cta: 'Nightly, early access' },
  { key: 'wearos',  device: 'watch',   name: 'WearOS',  kind: 'Watch · Play Store', tone: 'play',    chip: 'On Google Play', fmt: 'Wrist voice + glance · pairs with Android',     href: DOWNLOAD_PATHS.android,   cta: 'Open in Play' },
];

const CHIP_SX = {
  stable:    { color: '#7ff0c0', bgcolor: 'rgba(23,192,127,0.14)', border: '1px solid rgba(23,192,127,0.4)' },
  installer: { color: '#c9c2ff', bgcolor: 'rgba(124,114,255,0.14)', border: '1px solid rgba(124,114,255,0.42)' },
  play:      { color: '#a9e6ff', bgcolor: 'rgba(52,211,255,0.12)', border: '1px solid rgba(52,211,255,0.4)' },
  nightly:   { color: '#f4cf8a', bgcolor: 'rgba(240,178,58,0.12)', border: '1px solid rgba(240,178,58,0.42)' },
  soon:      { color: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' },
};

// A small device glyph so the matrix reads at a glance, not as a wall of text.
function DeviceGlyph({ device }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (device === 'phone') return (<svg {...common}><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M10 18.5h4" /></svg>);
  if (device === 'watch') return (<svg {...common}><rect x="7" y="6" width="10" height="12" rx="3" /><path d="M9 6l1-3h4l1 3M9 18l1 3h4l1-3" /></svg>);
  return (<svg {...common}><rect x="3" y="4" width="18" height="13" rx="1.5" /><path d="M8 20h8M12 17v3" /></svg>);
}

// OS-scoped editions of HART OS. Each of the three run methods comes in these
// three editions. Em dashes deliberately avoided in every rendered string.
const HARTOS_EDITIONS = [
  {
    label: 'Desktop',
    tag: 'GUI',
    who: 'A workstation you sit at. Full desktop, the agent stack, GPU drivers.',
    links: [
      { label: 'iso torrent', file: 'hart-os-1.0.0-desktop-x86_64-linux.iso.torrent', strong: true },
      { label: 'raw torrent', file: 'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.torrent', strong: false },
    ],
    viaInstaller: true,
  },
  {
    label: 'Server',
    who: 'A machine that serves other machines. Headless, no desktop.',
    links: [
      { label: 'ISO', file: 'hart-os-1.0.0-server-x86_64-linux.iso', strong: true },
      { label: 'sha256', file: 'hart-os-1.0.0-server-x86_64-linux.iso.sha256', strong: false },
      { label: 'torrent', file: 'hart-os-1.0.0-server-x86_64-linux.iso.torrent', strong: false },
    ],
  },
  {
    label: 'Edge',
    who: 'Small hardware at the edge of the network. The smallest image.',
    links: [
      { label: 'ISO', file: 'hart-os-1.0.0-edge-x86_64-linux.iso', strong: true },
      { label: 'sha256', file: 'hart-os-1.0.0-edge-x86_64-linux.iso.sha256', strong: false },
      { label: 'torrent', file: 'hart-os-1.0.0-edge-x86_64-linux.iso.torrent', strong: false },
    ],
  },
];

// Developer artifacts: the split desktop parts, reassemble scripts, checksums,
// the raw Python flasher, and the source / nightly / PR entry points. A common
// user never needs these; the installer above joins the parts for them.
const HARTOS_DEV = {
  desktop: [
    {
      kind: 'Installed image (.raw.xz)',
      parts: [
        'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.part-00',
        'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.part-01',
        'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.part-02',
        'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.part-03',
      ],
      reassemble: 'hart-os-1.0.0-desktop-x86_64-linux.raw.xz.reassemble.sh',
      sha256: 'hart-os-1.0.0-desktop-x86_64-linux.raw.sha256',
    },
    {
      kind: 'Live / installer (.iso)',
      parts: [
        'hart-os-1.0.0-desktop-x86_64-linux.iso.part-00',
        'hart-os-1.0.0-desktop-x86_64-linux.iso.part-01',
        'hart-os-1.0.0-desktop-x86_64-linux.iso.part-02',
        'hart-os-1.0.0-desktop-x86_64-linux.iso.part-03',
      ],
      reassemble: 'hart-os-1.0.0-desktop-x86_64-linux.iso.reassemble.sh',
      sha256: 'hart-os-1.0.0-desktop-x86_64-linux.iso.sha256',
    },
  ],
  links: [
    { label: 'All releases', href: 'https://github.com/hertz-ai/HARTOS/releases' },
    { label: 'Latest nightly', href: 'https://github.com/hertz-ai/HARTOS/releases/latest' },
    { label: 'Open pull requests', href: 'https://github.com/hertz-ai/HARTOS/pulls' },
    { label: 'Build status', href: 'https://github.com/hertz-ai/HARTOS/actions' },
  ],
};

// The funnel past install. Live paths are real links; the rest are marked "In
// build" honestly rather than shipped as dead links (each has a tracked task).
const FUNNEL = [
  { key: 'contribute', label: 'contribute', title: 'Host & contribute', body: 'Lend compute or run a node. Help build HART OS and Nunba in the open.', href: 'https://github.com/hertz-ai', cta: 'Start on GitHub' },
  { key: 'earn',       label: 'earn',       title: 'Start earning',     body: 'Seed a Hevolve endpoint or lend compute for Spark. Real potential for students.', to: '/earn', cta: 'See how to earn' },
  { key: 'jobs',       label: 'jobs',       title: 'Get things done',   body: 'Put agents to work on real tasks, or take on jobs other people need done.', to: '/jobs', cta: 'See what agents can do' },
  { key: 'refer',      label: 'refer',      title: 'Refer people in',   body: 'Share Nunba with a tagged link. Every install traces back to you, credited.', to: '/join', cta: 'Get your invite link' },
];

const FEATURES = [
  { title: 'Multimodal chat', body: 'Talk, type, share images. Local Qwen3-4B + 0.8B draft model gives sub-second first-token; speculative decoding handles speed.' },
  { title: 'Voice that sounds human', body: 'F5, Kokoro, Indic Parler and CosyVoice give GPU-tiered TTS, with Piper as the CPU fallback. Whisper STT for input.' },
  { title: 'Sees your screen / camera (with consent)', body: 'MiniCPM-V multimodal vision. Toggle anytime; nothing leaves your machine until you ask.' },
  { title: 'Channels you already use', body: '31 adapters: Discord, Slack, Telegram, WhatsApp, Teams, Matrix, Reddit and more. Your AI joins your rooms with your consent.' },
  { title: 'Federated hive (optional)', body: 'Your agent gets better from cumulative experience across the network, without sending your data to the cloud.' },
  { title: 'Free + open source', body: 'No subscription. No metered tokens. Source code on GitHub.' },
];

const SYSTEM_REQS = [
  { label: 'OS', value: 'Windows 10 / 11 (64-bit) native. macOS and Linux via the universal installer. Android and WearOS on Google Play. iOS nightly.' },
  { label: 'CPU', value: '4-core x86-64 (Intel / AMD)' },
  { label: 'RAM', value: '8 GB minimum, 16 GB recommended' },
  { label: 'Disk', value: '20 GB free (models + cache)' },
  { label: 'GPU', value: 'Optional. NVIDIA 6+ GB VRAM enables the draft model and faster TTS. CPU-only mode works.' },
  { label: 'Internet', value: 'Required for the first-run model download (~6 GB). Runs offline after.' },
];

const NAV = [
  { id: 'apps', label: 'Apps' },
  { id: 'hartos', label: 'HART OS' },
  { id: 'developers', label: 'Developers' },
  { id: 'more', label: 'Earn & refer' },
];

function Download() {
  // Attribution + OS-awareness are applied AFTER mount, on the DOM, never via
  // React state. This page is prerendered and lazy-loaded, and a state update
  // mid-hydration is React #421 ("received an update before it finished
  // hydrating"). Touching the DOM directly means React renders once, the markup
  // matches the prerendered file exactly, the OS swap and campaign params still
  // land, and clean /go/ URLs stay in the prerendered HTML for crawlers.
  const scopeRef = React.useRef(null);
  React.useEffect(() => {
    const scope = scopeRef.current || document;

    // OS-adapt the two primary CTAs. Every swapped href is a verified-200
    // asset, so this never points a visitor at a 404. Windows sees no change.
    try {
      const os = detectOS();
      const pcfg = PRIMARY_BY_OS[os];
      if (pcfg) {
        // Both the hero and the closing CTA carry data-dl="primary"; adapt
        // every one so a non-Windows visitor never sees "Download for Windows"
        // anywhere on the page.
        scope.querySelectorAll('[data-dl="primary"]').forEach((primary) => {
          primary.setAttribute('href', pcfg.href);
          const lbl = primary.querySelector('[data-dl-label]');
          if (lbl) lbl.textContent = pcfg.label;
        });
      }
      const inst = scope.querySelector('[data-dl="installer"]');
      const icfg = HARTOS_INSTALLERS[os];
      if (inst && icfg) {
        inst.setAttribute('href', `/go/hartos/${icfg.file}`);
        const ilbl = inst.querySelector('[data-dl-label]');
        if (ilbl) ilbl.textContent = icfg.label;
      }
      // "Install it as my OS" makes no sense on a phone: hide just that card.
      if (os === 'android' || os === 'ios') {
        const card = scope.querySelector('[data-dl="installer-card"]');
        if (card) card.style.display = 'none';
      }
    } catch (e) { /* best-effort: the Windows default already renders */ }

    // Carry ?ref/c/t onto every /go/ link (including the OS-rewritten ones) so
    // the click stays attributable to the channel that produced it.
    try {
      const p = new URLSearchParams(window.location.search);
      const keep = new URLSearchParams();
      ['ref', 'c', 't'].forEach((k) => { if (p.get(k)) keep.set(k, p.get(k)); });
      const s = keep.toString();
      if (!s) return;
      scope.querySelectorAll('a[href^="/go/"]').forEach((a) => {
        const href = a.getAttribute('href');
        if (href && href.indexOf('?') === -1) a.setAttribute('href', `${href}?${s}`);
      });
    } catch { /* attribution is best-effort, never break the page */ }
  }, []);

  const dl = (href) => href;

  // Download-conversion event: counts the actual download click against the
  // channel that referred this visitor (or 'direct'), so /marketing/growth can
  // see which channel converts landings into downloads, not just clicks.
  const trackDownload = () => {
    try {
      marketingApi.track(getReferralCode() || 'direct', 'download', 'web').catch(() => {});
    } catch { /* attribution is best-effort, never block the download */ }
  };

  // Copy the docker command, with feedback via the DOM (no state, so nothing
  // re-renders during hydration).
  const copyCmd = (e) => {
    const btn = e.currentTarget;
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(DOCKER_CMD);
      const prev = btn.textContent;
      btn.textContent = 'copied';
      setTimeout(() => { btn.textContent = prev; }, 1400);
    } catch (err) { /* clipboard blocked, no-op */ }
  };

  const card = {
    height: '100%', p: 3, borderRadius: 3,
    background: 'linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
    border: '1px solid rgba(255,255,255,0.09)',
    transition: 'transform .16s, border-color .2s, box-shadow .2s',
    '&:hover': { transform: 'translateY(-3px)', borderColor: 'rgba(255,255,255,0.18)', boxShadow: '0 18px 40px rgba(0,0,0,0.35)' },
  };

  return (
    <>
      <PageMeta
        title="Download Nunba & HART OS: Local-First AI for Every Device | Hevolve"
        description="Get Nunba, a free local-first multimodal AI agent, for Windows, macOS, Linux, Android, iOS and WearOS. Install HART OS on a USB or run it in Docker. Voice, vision, chat, 31 channels. Runs on your machine. Open source."
        ogTitle="Download Nunba & HART OS: Local-First AI for Every Device"
        ogDescription="Free, local-first multimodal AI. Windows, macOS, Linux, Android, iOS, WearOS, plus the whole HART OS. Runs on your machine. No subscription."
        path="/download"
      >
        <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'Nunba', operatingSystem: 'Windows 10, Windows 11, macOS, Linux, Android, iOS, WearOS', applicationCategory: 'BusinessApplication', downloadUrl: NUNBA_RELEASE_URL, offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }, publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' }, softwareVersion: 'latest', description: 'Local-first multimodal AI agent with voice, vision, chat and 31 channel integrations. Runs on your machine. No subscription. Open source.' })}</script>
      </PageMeta>
      <SeoHeader />
      <Box ref={scopeRef} sx={{ minHeight: '70vh', color: '#fff', pt: { xs: 6, md: 8 }, pb: 8,
        background: 'radial-gradient(1200px 620px at 80% -10%, rgba(124,114,255,0.16), transparent 60%), radial-gradient(900px 520px at 5% 2%, rgba(52,211,255,0.07), transparent 55%), #0B0A12' }}>
        <Container maxWidth="lg">

          {/* Hero */}
          <Stack spacing={2} sx={{ mb: 5 }}>
            <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>
              Local-first · runs on your machine
            </Typography>
            <Typography component="h1" variant="h2" sx={{ fontWeight: 800, letterSpacing: '-0.02em', background: 'linear-gradient(120deg, #ffffff, #cfc9ff 55%, #9fe9d0)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Download Nunba
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.85, maxWidth: 720 }}>
              Local-first multimodal AI on your machine: voice, vision and chat. Free. No subscription. Open source. HART OS is the whole machine underneath it.
            </Typography>
            <Box sx={{ mt: 1.5 }}><InstallCounter variant="hero" /></Box>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              <Chip label="Free forever" sx={{ background: '#17c07f', color: '#04140d', fontWeight: 700 }} />
              <Chip label="Local-first" sx={{ bgcolor: 'rgba(124,114,255,0.18)', color: '#cfcaff', fontWeight: 600 }} />
              <Chip label="Privacy by design" sx={{ bgcolor: 'rgba(124,114,255,0.18)', color: '#cfcaff', fontWeight: 600 }} />
              <Chip label="Open source" sx={{ bgcolor: 'rgba(124,114,255,0.18)', color: '#cfcaff', fontWeight: 600 }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Button component="a" data-dl="primary" href={dl(NUNBA_DOWNLOAD_URL)} onClick={trackDownload} variant="contained" size="large"
                sx={{ background: '#17c07f', color: '#04140d', '&:hover': { background: '#12a86e' }, borderRadius: 3, px: 5, py: 1.75, fontSize: '1.1rem', textTransform: 'none', fontWeight: 800 }}>
                <Box component="span" data-dl-label>Download for Windows</Box>
              </Button>
              <Button component="a" href="#apps" variant="outlined" size="large"
                sx={{ borderColor: 'rgba(255,255,255,0.28)', color: '#fff', '&:hover': { borderColor: '#7c72ff', bgcolor: 'rgba(124,114,255,0.08)' }, borderRadius: 3, px: 4, py: 1.75, fontSize: '1.05rem', textTransform: 'none', fontWeight: 600 }}>
                See all devices
              </Button>
              <Button component={Link} to="/" variant="text" size="large"
                sx={{ color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' }, textTransform: 'none', fontSize: '1.05rem', fontWeight: 600 }}>
                Try the cloud demo first &rarr;
              </Button>
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              It learns on your machine and keeps what it learns.{' '}
              <Box component={Link} to="/how_it_works" data-testid="download-how-it-works-link"
                sx={{ color: '#8b82ff', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                See how it works &rarr;
              </Box>
            </Typography>
          </Stack>

          {/* In-page nav so nobody gets lost in the plethora */}
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2, pb: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {NAV.map((n) => (
              <Box key={n.id} component="a" href={`#${n.id}`}
                sx={{ textDecoration: 'none', fontFamily: 'monospace', fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999, px: 1.75, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>
                {n.label}
              </Box>
            ))}
          </Stack>

          {/* ===== APPS: device matrix ===== */}
          <Box id="apps" sx={{ pt: 4 }}>
            <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>01 · The app</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>Nunba, on every device you use</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 760, mt: 1, mb: 3 }}>
              One companion, everywhere you are. Every tile shows exactly what ships today, so nothing you click is a dead end.
            </Typography>
            <Grid container spacing={2}>
              {DEVICES.map((d) => (
                <Grid item xs={12} sm={6} md={4} key={d.key}>
                  <Box sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 190 }}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#8b82ff' }}>
                        <DeviceGlyph device={d.device} />
                      </Box>
                      <Box component="span" sx={{ ...CHIP_SX[d.tone], fontFamily: 'monospace', fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', px: 1.1, py: 0.4, borderRadius: 999 }}>{d.chip}</Box>
                    </Stack>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{d.name}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.45)' }}>{d.kind}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)', mt: 'auto' }}>{d.fmt}</Typography>
                    <Box>
                      {d.href ? (
                        <Button component="a" href={dl(d.href)} onClick={trackDownload} size="small"
                          sx={{ background: '#17c07f', color: '#04140d', fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 2, '&:hover': { background: '#12a86e' } }}>
                          {d.cta}
                        </Button>
                      ) : (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.5)' }}>{d.cta}</Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" sx={{ opacity: 0.55, mt: 2 }}>
              Signed native installers for Windows, macOS and Linux (.deb and AppImage). Prefer one binary that installs Nunba or writes HART OS to a USB? The universal installer is in the HART OS section below.
            </Typography>
          </Box>

          {/* ===== HART OS: three ways to run ===== */}
          <Box id="hartos" sx={{ pt: 8 }}>
            <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>02 · The machine</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>HART OS: pick how you want to run it</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 780, mt: 1, mb: 3 }}>
              Nunba is the app. HART OS is the operating system it was built for: the agent stack, model runtime and hive networking, ready to go. Three clear ways in, so you never have to guess which file to grab.
            </Typography>

            <Grid container spacing={2}>
              {/* Install it (raw) */}
              <Grid item xs={12} md={4}>
                <Box data-dl="installer-card" sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25, background: 'linear-gradient(160deg, rgba(23,192,127,0.10), rgba(255,255,255,0.02))', border: '1px solid rgba(23,192,127,0.3)' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: '#17c07f' }}>Make it your OS</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Install it</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', minHeight: 66 }}>
                    Writes the installed image (raw) to a USB, boots as your OS with a writable root, and updates over the air. Flash once.
                  </Typography>
                  <Button component="a" data-dl="installer" href={hartos(HARTOS_INSTALLERS.windows.file)} onClick={trackDownload} variant="contained"
                    sx={{ mt: 'auto', background: '#17c07f', color: '#04140d', fontWeight: 700, textTransform: 'none', borderRadius: 2, py: 1.25, '&:hover': { background: '#12a86e' } }}>
                    <Box component="span" data-dl-label>{HARTOS_INSTALLERS.windows.label}</Box>
                  </Button>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.45)' }}>Double-click, no Python. exe, macOS and Linux, all signed.</Typography>
                </Box>
              </Grid>

              {/* Run it live (iso) */}
              <Grid item xs={12} md={4}>
                <Box sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25, background: 'linear-gradient(160deg, rgba(124,114,255,0.10), rgba(255,255,255,0.02))', border: '1px solid rgba(124,114,255,0.3)' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: '#8b82ff' }}>Try before you commit</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Run it live</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', minHeight: 66 }}>
                    Boot the LiveOS ISO from a USB to try HART OS with nothing installed, or install it to disk from there. Works on older BIOS machines too.
                  </Typography>
                  <Button component="a" href={hartos('hart-os-1.0.0-desktop-x86_64-linux.iso.torrent')} onClick={trackDownload} variant="contained"
                    sx={{ mt: 'auto', background: '#7c72ff', color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: 2, py: 1.25, '&:hover': { background: '#6b61e6' } }}>
                    Get the live ISO
                  </Button>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.45)' }}>The installer can flash this for you, or write it yourself.</Typography>
                </Box>
              </Grid>

              {/* Docker */}
              <Grid item xs={12} md={4}>
                <Box sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25, background: 'linear-gradient(160deg, rgba(52,211,255,0.10), rgba(255,255,255,0.02))', border: '1px solid rgba(52,211,255,0.3)' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: '#34d3ff' }}>On your existing OS</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Run in Docker</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', minHeight: 66 }}>
                    One command on any machine that has Docker. The full agent runtime in a container, no reboot, no USB.
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#05040a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, px: 1.5, py: 1.25, overflowX: 'auto' }}>
                    <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#b7f5da', whiteSpace: 'nowrap' }}>{DOCKER_CMD}</Typography>
                    <Box component="button" type="button" onClick={copyCmd}
                      sx={{ ml: 'auto', flex: '0 0 auto', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1, py: 0.5, '&:hover': { color: '#fff' } }}>copy</Box>
                  </Box>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.45)' }}>Or run the installer in <Box component="span" sx={{ color: '#34d3ff' }}>--mode docker</Box>.</Typography>
                </Box>
              </Grid>
            </Grid>

            {/* editions */}
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 0.5 }}>Choose an edition</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2 }}>
              Each of the three ways above comes in three editions. Same OS, sized for the machine.
            </Typography>
            <Grid container spacing={2}>
              {HARTOS_EDITIONS.map((ed) => (
                <Grid item xs={12} md={4} key={ed.label}>
                  <Box sx={{ ...card, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {ed.label}
                      {ed.tag && <Box component="span" sx={{ ...CHIP_SX.stable, fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 700, px: 0.9, py: 0.3, borderRadius: 999 }}>{ed.tag}</Box>}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', minHeight: 44, mt: 0.5 }}>{ed.who}</Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                      {ed.viaInstaller && (
                        <Box component="span" sx={{ ...CHIP_SX.installer, fontFamily: 'monospace', fontSize: '0.66rem', fontWeight: 600, px: 1, py: 0.4, borderRadius: 999 }}>via installer</Box>
                      )}
                      {ed.links.map((lk) => (
                        <Typography key={lk.file} component="a" href={hartos(lk.file)} onClick={trackDownload} variant="caption"
                          sx={{ fontFamily: 'monospace', color: lk.strong ? '#17c07f' : 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: '#17c07f' } }}>
                          {lk.label}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* developer artifacts, quiet */}
            <Box sx={{ mt: 3, p: 3, borderRadius: 3, background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.14)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'rgba(255,255,255,0.85)' }}>Assemble it yourself (developers)</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 2, maxWidth: 760 }}>
                The desktop image ships in four parts because GitHub caps assets at 2 GB. Grab all four plus the reassemble script, or just use the installer or a torrent above. Prefer a script over a binary? The raw <code>hart_usb_flasher.py</code> (needs Python 3) is here too. Nobody needs any of this for the paths above.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography component="a" href={hartos(HARTOS_FLASHER)} onClick={trackDownload} variant="caption"
                  sx={{ fontFamily: 'monospace', color: '#17c07f', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  hart_usb_flasher.py (raw script)
                </Typography>
              </Box>
              {HARTOS_DEV.desktop.map((form) => (
                <Box key={form.kind} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>Desktop · {form.kind}</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {form.parts.map((file, i) => (
                      <Typography key={file} component="a" href={hartos(file)} variant="caption"
                        sx={{ fontFamily: 'monospace', color: '#17c07f', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>part {i + 1}</Typography>
                    ))}
                    <Typography component="a" href={hartos(form.reassemble)} variant="caption"
                      sx={{ fontFamily: 'monospace', color: '#17c07f', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>reassemble.sh</Typography>
                    <Typography component="a" href={hartos(form.sha256)} variant="caption"
                      sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: '#17c07f' } }}>sha256</Typography>
                  </Stack>
                </Box>
              ))}
              <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                {HARTOS_DEV.links.map((l) => (
                  <Typography key={l.href} component="a" href={l.href} target="_blank" rel="noopener noreferrer" variant="caption"
                    sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: '#17c07f' } }}>{l.label} &#8599;</Typography>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* ===== DEVELOPERS ===== */}
          <Box id="developers" sx={{ pt: 8 }}>
            <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>03 · Build on it</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>Developers: embed it, call it, contribute</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 780, mt: 1, mb: 3 }}>
              The same intelligence you install is an endpoint you can build on. Drop the widget into any page, call the API from your stack, or help build the hive itself.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ ...card }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Embed the widget</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
                    A full agent in any website, one tag. It runs a rate-limited guest trial, so visitors can talk to it with no signup.
                  </Typography>
                  <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.76rem', lineHeight: 1.7, bgcolor: '#05040a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2, overflowX: 'auto', color: '#d7d3ea' }}>
{`<iframe
  src="https://hevolve.ai/agents/your-agent?plugin=1"
  style="width:380px;height:560px;border:0">
</iframe>`}
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography component={Link} to="/agents" variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1.25, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>Browse agents</Typography>
                    <Typography component={Link} to="/docs/widget" variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1.25, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>Widget docs</Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ ...card }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Call the API</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
                    An intelligence endpoint with a free tier (100 requests a day) and an SDK per surface: chat, analyze, generate, keys. The full SDK, API reference and widget guide are in the docs.
                  </Typography>
                  <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.76rem', lineHeight: 1.7, bgcolor: '#05040a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, p: 2, overflowX: 'auto', color: '#d7d3ea' }}>
{`curl https://hevolve.ai/api/v1/intelligence/chat \\
  -H "Authorization: Bearer $KEY" \\
  -d '{"prompt":"summarize this"}'`}
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography component={Link} to="/docs" variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1.25, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>Read the docs</Typography>
                    <Typography component={Link} to="/pricing" variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1.25, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>Get an API key</Typography>
                    <Typography component="a" href="https://github.com/hertz-ai" target="_blank" rel="noopener noreferrer" variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1.5, px: 1.25, py: 0.75, '&:hover': { color: '#fff', borderColor: '#7c72ff' } }}>Contribute on GitHub</Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* ===== EARN / JOBS / REFER ===== */}
          <Box id="more" sx={{ pt: 8 }}>
            <Typography variant="overline" sx={{ color: '#8b82ff', fontWeight: 700, letterSpacing: 2 }}>04 · Do more with it</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em', mt: 0.5 }}>Earn, hire, and grow the hive</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 780, mt: 1, mb: 3 }}>
              Installing is the start. Live paths are linked; the rest are in build and marked honestly, so nothing here is a dead end.
            </Typography>
            <Grid container spacing={2}>
              {FUNNEL.map((f) => (
                <Grid item xs={12} sm={6} md={3} key={f.key}>
                  <Box sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 186, borderColor: (f.href || f.to) ? 'rgba(124,114,255,0.3)' : 'rgba(255,255,255,0.09)' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>{f.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{f.title}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{f.body}</Typography>
                    <Box sx={{ mt: 'auto' }}>
                      {f.to ? (
                        <Typography component={Link} to={f.to} sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8b82ff', textDecoration: 'none', '&:hover': { color: '#fff' } }}>{f.cta} &rarr;</Typography>
                      ) : f.href ? (
                        <Typography component="a" href={f.href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#8b82ff', textDecoration: 'none', '&:hover': { color: '#fff' } }}>{f.cta} &rarr;</Typography>
                      ) : (
                        <Box component="span" sx={{ ...CHIP_SX.soon, fontFamily: 'monospace', fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', px: 1.1, py: 0.4, borderRadius: 999 }}>In build</Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <GrowthFunnel />

          {/* ===== What you get ===== */}
          <Typography variant="h4" sx={{ mt: 8, mb: 3, fontWeight: 700 }}>What you get</Typography>
          <Grid container spacing={2.5} sx={{ mb: 6 }}>
            {FEATURES.map((f) => (
              <Grid item xs={12} sm={6} md={4} key={f.title}>
                <Box sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(124,114,255,0.06)', border: '1px solid rgba(124,114,255,0.18)', height: '100%' }}>
                  <Typography variant="h6" sx={{ color: '#ff8f8f', mb: 1, fontWeight: 600 }}>{f.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)' }}>{f.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* ===== System requirements ===== */}
          <Typography variant="h4" sx={{ mt: 6, mb: 3, fontWeight: 700 }}>System requirements</Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(124,114,255,0.18)', mb: 6 }}>
            {SYSTEM_REQS.map((r, i) => (
              <Stack key={r.label} direction={{ xs: 'column', sm: 'row' }} sx={{ px: 3, py: 2, bgcolor: i % 2 === 0 ? 'rgba(124,114,255,0.04)' : 'transparent', gap: { xs: 0.5, sm: 0 } }}>
                <Typography sx={{ minWidth: 120, fontWeight: 600, color: '#cfcaff' }}>{r.label}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.84)' }}>{r.value}</Typography>
              </Stack>
            ))}
          </Box>

          {/* Final CTA */}
          <Box sx={{ mt: 8, p: 5, borderRadius: 3, bgcolor: 'rgba(23,192,127,0.08)', border: '1px solid #17c07f', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>Ready?</Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8, maxWidth: 600, mx: 'auto' }}>
              Your AI lives on your machine. You own it. It gets better with you, not for someone else's quarterly numbers.
            </Typography>
            <Button component="a" data-dl="primary" href={dl(NUNBA_DOWNLOAD_URL)} onClick={trackDownload} variant="contained" size="large"
              sx={{ background: '#17c07f', color: '#04140d', '&:hover': { background: '#12a86e' }, borderRadius: 3, px: 6, py: 2, fontSize: '1.2rem', textTransform: 'none', fontWeight: 800 }}>
              <Box component="span" data-dl-label>Download Nunba for Windows</Box>
            </Button>
          </Box>
        </Container>
      </Box>
      <SeoFooter />
    </>
  );
}

// OS detection, client only. Never called during render (that would diverge
// from the prerendered HTML and cause a hydration mismatch); the effect calls
// it after mount. Falls back to windows so the default paint is stable.
function detectOS() {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    const plat = (navigator.platform || '').toLowerCase();
    if (/android/.test(ua)) return 'android';
    if (/iphone|ipad|ipod/.test(ua) || (plat === 'macintel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/win/.test(plat) || /windows/.test(ua)) return 'windows';
    if (/mac/.test(plat) || /mac os x/.test(ua)) return 'mac';
    if (/linux/.test(plat) || /linux/.test(ua)) return 'linux';
  } catch (e) { /* no navigator: keep the windows default */ }
  return 'windows';
}

export default Download;
export { NUNBA_DOWNLOAD_URL };
